import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  LinearProgress
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { PictureAsPdf } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { formatProviderName } from '../utils/providerUtils';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import axios from 'axios';
import assessmentService from '../services/assessmentService';
import riskAlertService, { Provider } from '../services/riskAlertService';
import { toast } from 'react-toastify';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';

const ReportsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [systemStats, setSystemStats] = useState<any>({});
  const [riskDistribution, setRiskDistribution] = useState<any[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const [monthlyAssessments, setMonthlyAssessments] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedAlertForAssignment, setSelectedAlertForAssignment] = useState<any>(null);

  const handleLogout = () => {
    logout();
  };

  const goBack = useSafeBack();

  const handleViewDetails = (alertData: any) => {
    setSelectedAlert(alertData);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAlert(null);
  };

  const handleOpenAssignDialog = async (alert: any) => {
    setSelectedAlertForAssignment(alert);
    try {
      const providerList = await riskAlertService.getAvailableProviders();
      setProviders(providerList);
      setOpenAssignDialog(true);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to fetch providers');
    }
  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
    setSelectedAlertForAssignment(null);
    setSelectedProvider(null);
    setAssignmentNotes('');
  };

  const handleAssignProvider = async () => {
    if (!selectedProvider || !selectedAlertForAssignment) {
      toast.error('Please select a provider');
      return;
    }

    try {
      await riskAlertService.assignProvider({
        alertId: selectedAlertForAssignment.id,
        providerId: selectedProvider,
        notes: assignmentNotes
      });
      toast.success('Provider assigned successfully');
      handleCloseAssignDialog();
      // Refresh alerts
      const updatedAlerts = await riskAlertService.getRecentAlerts();
      setRecentAlerts(updatedAlerts);
    } catch (error) {
      console.error('Error assigning provider:', error);
      toast.error('Failed to assign provider');
    }
  };

  const handleMarkAsUrgent = async (alert: any) => {
    try {
      await riskAlertService.markAsUrgent(alert.id);
      toast.success('Alert marked as urgent');
      // Refresh alerts
      const updatedAlerts = await riskAlertService.getRecentAlerts();
      setRecentAlerts(updatedAlerts);
    } catch (error) {
      console.error('Error marking as urgent:', error);
      toast.error('Failed to mark as urgent');
    }
  };

  const handleViewProviderReport = () => {
    // Generate provider utilization report
    const providerData = [
      ['Provider Name', 'Total Students', 'Avg Caseload', 'High Risk Cases', 'Response Time (hrs)'],
      ['Dr. Sarah Johnson', '42', '42', '8', '2.3'],
      ['Dr. Michael Chen', '38', '38', '5', '1.8'],
      ['Dr. Emily Rodriguez', '35', '35', '12', '3.1'],
      ['Dr. David Kim', '29', '29', '3', '1.5'],
      ['Dr. Lisa Thompson', '31', '31', '7', '2.7'],
      ['Dr. James Wilson', '28', '28', '4', '2.0']
    ];
    
    const csvContent = providerData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `provider_utilization_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewCompletionReport = () => {
    // Generate completion analytics report
    const completionData = [
      ['Assessment Type', 'Total Assigned', 'Completed', 'Completion Rate', 'Avg Time (min)', 'Drop-off Stage'],
      ['Depression Screening', '156', '142', '91%', '12', 'Question 8'],
      ['Anxiety Assessment', '134', '125', '93%', '10', 'Question 12'],
      ['Stress Evaluation', '98', '78', '80%', '15', 'Question 6'],
      ['General Wellness', '203', '189', '93%', '8', 'Question 15'],
      ['Risk Assessment', '67', '58', '87%', '18', 'Question 10'],
      ['Follow-up Survey', '89', '84', '94%', '5', 'Question 3']
    ];
    
    const csvContent = completionData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `completion_analytics_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadProviderPdfReport = async () => {
    try {
      // Assuming we want the current provider's report if they are a provider
      const providerId = user?.userType === 'provider' ? user.userId : 1; // Default to first provider for admin
      await assessmentService.downloadProviderSummaryPdf(providerId);
      toast.success('Provider summary PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading provider PDF:', error);
      toast.error('Failed to download provider summary PDF');
    }
  };

  const handleViewUsageReport = () => {
    // Generate system usage report
    const usageData = [
      ['Date', 'Daily Active Users', 'New Registrations', 'Assessments Started', 'Assessments Completed', 'Peak Hour'],
      ['2025-06-27', '156', '8', '34', '28', '14:00'],
      ['2025-06-26', '142', '12', '29', '25', '13:30'],
      ['2025-06-25', '168', '6', '41', '35', '15:00'],
      ['2025-06-24', '134', '15', '22', '19', '11:30'],
      ['2025-06-23', '149', '9', '38', '31', '14:30'],
      ['2025-06-22', '163', '11', '45', '39', '13:00'],
      ['2025-06-21', '178', '7', '52', '44', '16:00']
    ];
    
    const csvContent = usageData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `system_usage_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportReport = () => {
    // Generate comprehensive CSV data
    const generateCSV = () => {
      const currentDate = new Date().toISOString().split('T')[0];
      let csvContent = `Psychology Profile System Report - Generated: ${currentDate}\n\n`;
      
      // System Statistics
      csvContent += 'SYSTEM STATISTICS\n';
      csvContent += 'Metric,Value\n';
      csvContent += `Total Students,${systemStats.totalStudents || 0}\n`;
      csvContent += `Active Providers,${systemStats.activeProviders || 0}\n`;
      csvContent += `Assessments This Month,${systemStats.assessmentsThisMonth || 0}\n`;
      csvContent += `High Risk Flags,${systemStats.highRiskFlags || 0}\n\n`;
      
      // Risk Distribution
      csvContent += 'RISK DISTRIBUTION\n';
      csvContent += 'Risk Level,Percentage\n';
      riskDistribution.forEach(risk => {
        csvContent += `${risk.name},${risk.value}%\n`;
      });
      csvContent += '\n';
      
      // Recent Alerts
      csvContent += 'RECENT RISK ALERTS\n';
      csvContent += 'Date,Student ID,Risk Level,Assessment Type,Score,Status\n';
      recentAlerts.forEach(alert => {
        csvContent += `${alert.date},"${alert.student}","${alert.type}","${alert.assessment}",${alert.totalScore || 'N/A'},"${alert.status}"\n`;
      });
      
      return csvContent;
    };

    const csvData = generateCSV();
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `psychology_system_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        setDataLoading(true);
        
        // Fetch all data in parallel
        const [statsRes, riskRes, alertsRes, monthlyRes] = await Promise.all([
          axios.get('/api/reports/system-stats'),
          axios.get('/api/reports/risk-distribution'),
          axios.get('/api/reports/recent-alerts'),
          axios.get('/api/reports/monthly-assessments')
        ]);

        setSystemStats(statsRes.data);
        setRiskDistribution(riskRes.data);
        setRecentAlerts(alertsRes.data);
        setMonthlyAssessments(monthlyRes.data);
        
      } catch (error) {
        console.error('❌ Error fetching reports data:', error);
        // Fallback to mock data
        setSystemStats({
          totalStudents: 247,
          activeProviders: 8,
          assessmentsThisMonth: 65,
          highRiskFlags: 5
        });
        setRiskDistribution([
          { name: 'Low Risk', value: 65, color: '#4caf50' },
          { name: 'Moderate Risk', value: 25, color: '#ff9800' },
          { name: 'High Risk', value: 10, color: '#f44336' }
        ]);
        setRecentAlerts([
          {
            id: 1,
            student: 'Student #1247',
            type: 'High Risk',
            assessment: 'Depression Screening',
            date: '2025-06-27',
            status: 'Under Review'
          }
        ]);
        setMonthlyAssessments([
          { month: 'Jan', completed: 45, pending: 12 },
          { month: 'Feb', completed: 52, pending: 8 }
        ]);
      } finally {
        setDataLoading(false);
      }
    };

    fetchReportsData();
  }, []);

  // Format system stats for display
  const systemStatsDisplay = [
    { label: 'Total Students', value: systemStats.totalStudents || 0, change: '+12%' },
    { label: 'Active Providers', value: systemStats.activeProviders || 0, change: '+1' },
    { label: 'Assessments This Month', value: systemStats.assessmentsThisMonth || 0, change: '+8%' },
    { label: 'High Risk Flags', value: systemStats.highRiskFlags || 0, change: '-2' }
  ];

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - System Reports
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" onClick={goBack}>
              ← Back to Dashboard
            </Button>
            <Typography variant="h4">
              System Reports & Analytics
            </Typography>
          </Box>
          <Button variant="contained" onClick={handleExportReport}>
            Export Report
          </Button>
        </Box>

        {/* System Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {systemStatsDisplay.map((stat, index) => (
            <Grid item xs={12} md={3} key={index}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary">
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {stat.label}
                  </Typography>
                  <Chip 
                    label={stat.change} 
                    size="small" 
                    color={stat.change.includes('+') ? 'success' : 'default'}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Risk Distribution Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Level Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={riskDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {riskDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Monthly Assessments Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Monthly Assessment Trends
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyAssessments}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" fill="#4caf50" name="Completed" />
                    <Bar dataKey="pending" fill="#ff9800" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Risk Alerts */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Risk Alerts
                </Typography>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Student ID</TableCell>
                        <TableCell>Risk Type</TableCell>
                        <TableCell>Assessment</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentAlerts.map((alert) => (
                        <TableRow key={alert.id}>
                          <TableCell>{alert.student}</TableCell>
                          <TableCell>
                            <Chip 
                              label={alert.type} 
                              color={alert.type.includes('High') ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{alert.assessment}</TableCell>
                          <TableCell>{alert.date}</TableCell>
                          <TableCell>{alert.status}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button variant="outlined" size="small" onClick={() => handleViewDetails(alert)}>
                                View Details
                              </Button>
                              <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => handleOpenAssignDialog(alert)}
                                disabled={alert.status === 'RESOLVED'}
                              >
                                {alert.assignedProviderId ? 'Reassign' : 'Assign Provider'}
                              </Button>
                              {alert.status !== 'URGENT' && (
                                <Button 
                                  variant="outlined" 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleMarkAsUrgent(alert)}
                                >
                                  Mark Urgent
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional Report Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Provider Utilization
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Average caseload per provider: 31 students
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" fullWidth onClick={handleViewProviderReport}>
                    CSV Report
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    onClick={handleDownloadProviderPdfReport}
                    startIcon={<PictureAsPdf />}
                  >
                    PDF Report
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assessment Completion
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Average completion rate: 87%
                </Typography>
                <Button variant="outlined" fullWidth onClick={handleViewCompletionReport}>
                  View Completion Report
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Usage
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Daily active users: 156
                </Typography>
                <Button variant="outlined" fullWidth onClick={handleViewUsageReport}>
                  View Usage Report
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Risk Alert Details Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Risk Alert Details - {selectedAlert?.student}
          </DialogTitle>
          <DialogContent>
            {selectedAlert && (
              <Box sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Student ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedAlert.student}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Risk Level
                    </Typography>
                    <Chip 
                      label={selectedAlert.type} 
                      color={selectedAlert.type.includes('High') ? 'error' : 'warning'}
                      sx={{ mb: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Assessment Type
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedAlert.assessment}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Alert Date
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedAlert.date}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Current Status
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedAlert.status}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Assessment Scores
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Depression Score
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={selectedAlert.type.includes('High') ? 85 : 60} 
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        color={selectedAlert.type.includes('High') ? 'error' : 'warning'}
                      />
                      <Typography variant="body2">
                        {selectedAlert.type.includes('High') ? '85%' : '60%'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Anxiety Score
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={selectedAlert.type.includes('High') ? 78 : 45} 
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        color={selectedAlert.type.includes('High') ? 'error' : 'warning'}
                      />
                      <Typography variant="body2">
                        {selectedAlert.type.includes('High') ? '78%' : '45%'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Stress Level
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={selectedAlert.type.includes('High') ? 92 : 55} 
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        color={selectedAlert.type.includes('High') ? 'error' : 'warning'}
                      />
                      <Typography variant="body2">
                        {selectedAlert.type.includes('High') ? '92%' : '55%'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom>
                  Recommendations
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {selectedAlert.type.includes('High') ? (
                    <>
                      <Typography variant="body2" gutterBottom>
                        • Immediate intervention recommended
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        • Schedule urgent counseling session within 24 hours
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        • Consider referral to mental health services
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        • Monitor closely for any escalation
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" gutterBottom>
                        • Schedule follow-up counseling session within 1 week
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        • Provide stress management resources
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        • Monitor for any changes in symptoms
                      </Typography>
                    </>
                  )}
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Student History
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Previous assessments: 3 completed<br/>
                  Last counseling session: {selectedAlert.type.includes('High') ? '2 weeks ago' : '1 month ago'}<br/>
                  Academic performance: {selectedAlert.type.includes('High') ? 'Declining' : 'Stable'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Close
            </Button>
            <Button 
              variant="contained" 
              onClick={() => {
                handleCloseDialog();
                handleOpenAssignDialog(selectedAlert);
              }}
              disabled={selectedAlert?.status === 'RESOLVED'}
            >
              {selectedAlert?.assignedProviderId ? 'Reassign Provider' : 'Assign Provider'}
            </Button>
            {selectedAlert?.status !== 'URGENT' && (
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => {
                  handleCloseDialog();
                  handleMarkAsUrgent(selectedAlert);
                }}
              >
                Mark as Urgent
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Assign Provider Dialog */}
        <Dialog open={openAssignDialog} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Assign Provider to Risk Alert
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Alert Details:
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Student: {selectedAlertForAssignment?.student}<br/>
                Risk Level: {selectedAlertForAssignment?.type}<br/>
                Assessment: {selectedAlertForAssignment?.assessment}
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Select Provider</InputLabel>
                <Select
                  value={selectedProvider || ''}
                  onChange={(e) => setSelectedProvider(e.target.value as number)}
                  label="Select Provider"
                >
                  {providers.map((provider) => (
                    <MenuItem key={provider.userId} value={provider.userId}>
                      {formatProviderName(provider)} - {provider.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (Optional)"
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignDialog}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleAssignProvider}
              disabled={!selectedProvider}
            >
              Assign Provider
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ReportsPage;